import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider';
import { useTranslation } from 'react-i18next';
import { X, Edit, Trash2, Plus, CheckCircle, AlertCircle } from 'lucide-react';

function ExamConfigManager() {
  const { examId } = useParams();
  const { api, user } = useAuth();
  const { t, i18n } = useTranslation('examconfigmanagement');
  
  const isRTL = i18n.language === 'ar';
  
  // State variables
  const [exam, setExam] = useState(null);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [configurations, setConfigurations] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleteClassConfigsModalOpen, setIsDeleteClassConfigsModalOpen] = useState(false);
  const [notification, setNotification] = useState({ show: false, type: '', message: '' });
  const [currentConfig, setCurrentConfig] = useState(null);
  const [formData, setFormData] = useState({
    class_id: '',
    subjectScores: []
  });
  const [classConfigToDelete, setClassConfigToDelete] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        await Promise.all([
          fetchExam(),
          fetchClasses(),
          fetchSubjects(),
          fetchConfigurations()
        ]);
        setLoading(false);
      } catch (error) {
        setNotification({
          show: true,
          type: 'error',
          message: t('errors.loadDataFailed')
        });
        setLoading(false);
      }
    };

    fetchData();
  }, [examId]);

  // Fetch exam details
  const fetchExam = async () => {
    try {
      const response = await api.get(`/api/v1/exams/${examId}/school/${user.school_id}`);
      setExam(response.data);
    } catch (error) {
      handleError(error, t('errors.loadExamFailed'));
    }
  };

  // Fetch classes
  const fetchClasses = async () => {
    try {
      const response = await api.get(`/api/v1/admin/schools/${user.school_reg}/classes`);
      setClasses(response.data);
    } catch (error) {
      handleError(error, t('errors.loadClassesFailed'));
    }
  };

  // Fetch subjects
  const fetchSubjects = async () => {
    try {
      const response = await api.get('/api/v1/subjects');
      setSubjects(response.data);
    } catch (error) {
      handleError(error, t('errors.loadSubjectsFailed'));
    }
  };

  // Fetch configurations
  const fetchConfigurations = async () => {
    try {
      const response = await api.get(`/api/v1/exam-configs/school/${user.school_id}/exam/${examId}`);
      setConfigurations(response.data);
    } catch (error) {
      handleError(error, t('errors.loadConfigurationsFailed'));
    }
  };

  // Handle error and display notification
  const handleError = (error, defaultMessage) => {
    const errorMessage = error.response?.data?.detail
      ? Array.isArray(error.response.data.detail)
        ? error.response.data.detail[0]?.msg || defaultMessage
        : error.response.data.detail
      : defaultMessage;
    
    setNotification({
      show: true,
      type: 'error',
      message: errorMessage
    });
  };

  // Show success notification
  const showSuccess = (message) => {
    setNotification({
      show: true,
      type: 'success',
      message
    });
    
    // Auto-hide notification after 3 seconds
    setTimeout(() => {
      setNotification({ show: false, type: '', message: '' });
    }, 3000);
  };

  // Reset form data
  const resetFormData = () => {
    setFormData({
      class_id: '',
      subjectScores: subjects.map(subject => ({
        subject_id: subject.id,
        maximum_score: 100
      }))
    });
  };

  // Open create modal
  const openCreateModal = () => {
    resetFormData();
    setIsCreateModalOpen(true);
  };

  // Handle class selection change for the create form
  const handleClassChange = (e) => {
    setFormData({
      ...formData,
      class_id: parseInt(e.target.value)
    });
  };

  // Handle score change for a subject
  const handleScoreChange = (subjectId, value) => {
    setFormData({
      ...formData,
      subjectScores: formData.subjectScores.map(item => 
        item.subject_id === subjectId ? { ...item, maximum_score: parseInt(value) } : item
      )
    });
  };

  // Submit create form
  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const { class_id, subjectScores } = formData;
      
      // Create a configuration for each subject
      const promises = subjectScores.map(subjectScore => 
        api.post(`/api/v1/exam-configs/school/${user.school_id}/exam/${examId}`, {
          subject_id: subjectScore.subject_id,
          class_id: class_id,
          maximum_score: subjectScore.maximum_score
        })
      );
      
      await Promise.all(promises);
      await fetchConfigurations();
      setIsCreateModalOpen(false);
      showSuccess(t('success.configurationsCreated'));
    } catch (error) {
      handleError(error, t('errors.createConfigurationsFailed'));
    }
  };

  // Open edit modal
  const openEditModal = (config) => {
    setCurrentConfig(config);
    setFormData({
      class_id: config.class_id,
      subjectScores: [{
        subject_id: config.subject_id,
        maximum_score: config.maximum_score
      }]
    });
    setIsEditModalOpen(true);
  };

  // Submit edit form
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await api.put(`/api/v1/exam-configs/school/${user.school_id}/exam/${examId}/config/${currentConfig.id}`, {
        subject_id: formData.subjectScores[0].subject_id,
        class_id: formData.class_id,
        maximum_score: formData.subjectScores[0].maximum_score
      });
      
      await fetchConfigurations();
      setIsEditModalOpen(false);
      showSuccess(t('success.configurationUpdated'));
    } catch (error) {
      handleError(error, t('errors.updateConfigurationFailed'));
    }
  };

  // Open delete confirmation modal
  const openDeleteModal = (config) => {
    setCurrentConfig(config);
    setIsDeleteModalOpen(true);
  };

  // Handle delete confirmation
  const handleDelete = async () => {
    try {
      await api.delete(`/api/v1/exam-configs/school/${user.school_id}/exam/${examId}/config/${currentConfig.id}`);
      await fetchConfigurations();
      setIsDeleteModalOpen(false);
      showSuccess(t('success.configurationDeleted'));
    } catch (error) {
      handleError(error, t('errors.deleteConfigurationFailed'));
    }
  };

  // Open delete class configs confirmation modal
  const openDeleteClassConfigsModal = (classId) => {
    setClassConfigToDelete(classId);
    setIsDeleteClassConfigsModalOpen(true);
  };

  // Handle delete class configs confirmation
  const handleDeleteClassConfigs = async () => {
    try {
      // Filter configurations by class_id
      const classConfigs = configurations.filter(config => config.class_id === classConfigToDelete);
      
      // Delete each configuration
      const promises = classConfigs.map(config => 
        api.delete(`/api/v1/exam-configs/school/${user.school_id}/exam/${examId}/config/${config.id}`)
      );
      
      await Promise.all(promises);
      await fetchConfigurations();
      setIsDeleteClassConfigsModalOpen(false);
      showSuccess(t('success.classConfigurationsDeleted'));
    } catch (error) {
      handleError(error, t('errors.deleteClassConfigurationsFailed'));
    }
  };

  // Handle filter by class
  const handleClassFilter = (classId) => {
    setSelectedClass(classId === selectedClass ? null : classId);
  };

  // Get class name by id
  const getClassName = (classId) => {
    const classObj = classes.find(cls => cls.id === classId);
    return classObj ? classObj.name : t('common.unknownClass');
  };

  // Get subject name by id
  const getSubjectName = (subjectId) => {
    const subject = subjects.find(sub => sub.id === subjectId);
    return subject ? subject.subject_name : t('common.unknownSubject');
  };

  // Group configurations by class
  const configsByClass = configurations.reduce((acc, config) => {
    if (!acc[config.class_id]) {
      acc[config.class_id] = [];
    }
    acc[config.class_id].push(config);
    return acc;
  }, {});

  // Filter configurations by selected class
  const filteredConfigs = selectedClass
    ? { [selectedClass]: configsByClass[selectedClass] }
    : configsByClass;

  // Render notification
  const renderNotification = () => {
    if (!notification.show) return null;
    
    return (
      <div className={`p-4 mb-4 rounded-md flex items-center justify-between ${
        notification.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
      }`}>
        <div className="flex items-center">
          {notification.type === 'success' ? (
            <CheckCircle className={`w-5 h-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
          ) : (
            <AlertCircle className={`w-5 h-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
          )}
          <span>{notification.message}</span>
        </div>
        <button onClick={() => setNotification({ show: false, type: '', message: '' })} className="text-gray-500 hover:text-gray-700">
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  };

  // Render create modal
  const renderCreateModal = () => {
    if (!isCreateModalOpen) return null;
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl max-h-screen overflow-y-auto">
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-800">{t('modals.createConfiguration.title')}</h2>
            <button onClick={() => setIsCreateModalOpen(false)} className="text-gray-500 hover:text-gray-700">
              <X className="w-6 h-6" />
            </button>
          </div>
          
          <form onSubmit={handleCreateSubmit} className="p-4">
            <div className="mb-4">
              <label className={`block text-sm font-medium text-gray-700 mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                {t('forms.selectClass')}
              </label>
              <select
                value={formData.class_id}
                onChange={handleClassChange}
                className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-500 ${isRTL ? 'text-right' : 'text-left'}`}
                required
              >
                <option value="">{t('forms.selectClassPlaceholder')}</option>
                {classes.map(cls => (
                  <option key={cls.id} value={cls.id}>{cls.name}</option>
                ))}
              </select>
            </div>
            
            {formData.class_id && (
              <div className="mb-4">
                <h3 className={`text-lg font-medium text-gray-700 mb-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('forms.setMaximumScores')}
                </h3>
                <div className="bg-gray-50 p-3 rounded-md">
                  {subjects.map(subject => (
                    <div key={subject.id} className={`mb-3 flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <span className={`w-1/2 text-sm font-medium text-gray-700 ${isRTL ? 'text-right pr-2' : 'text-left'}`}>
                        {subject.subject_name}
                      </span>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={formData.subjectScores.find(s => s.subject_id === subject.id)?.maximum_score || 100}
                        onChange={(e) => handleScoreChange(subject.id, e.target.value)}
                        className={`w-1/2 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-500 ${isRTL ? 'text-right' : 'text-left'}`}
                        required
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div className={`flex ${isRTL ? 'flex-row-reverse' : ''} justify-end mt-4`}>
              <button
                type="button"
                onClick={() => setIsCreateModalOpen(false)}
                className={`px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 ${isRTL ? 'ml-2' : 'mr-2'}`}
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                disabled={!formData.class_id}
                className={`px-4 py-2 text-sm font-medium text-white rounded-md shadow-sm ${
                  formData.class_id ? 'bg-teal-600 hover:bg-teal-700' : 'bg-gray-400 cursor-not-allowed'
                }`}
              >
                {t('buttons.createConfiguration')}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // Render edit modal
  const renderEditModal = () => {
    if (!isEditModalOpen || !currentConfig) return null;
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg shadow-lg w-full max-w-md">
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-800">{t('modals.editConfiguration.title')}</h2>
            <button onClick={() => setIsEditModalOpen(false)} className="text-gray-500 hover:text-gray-700">
              <X className="w-6 h-6" />
            </button>
          </div>
          
          <form onSubmit={handleEditSubmit} className="p-4">
            <div className="mb-4">
              <label className={`block text-sm font-medium text-gray-700 mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                {t('forms.class')}
              </label>
              <input
                type="text"
                value={getClassName(currentConfig.class_id)}
                className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 ${isRTL ? 'text-right' : 'text-left'}`}
                disabled
              />
            </div>
            
            <div className="mb-4">
              <label className={`block text-sm font-medium text-gray-700 mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                {t('forms.subject')}
              </label>
              <input
                type="text"
                value={getSubjectName(currentConfig.subject_id)}
                className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 ${isRTL ? 'text-right' : 'text-left'}`}
                disabled
              />
            </div>
            
            <div className="mb-4">
              <label className={`block text-sm font-medium text-gray-700 mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                {t('forms.maximumScore')}
              </label>
              <input
                type="number"
                min="0"
                max="100"
                value={formData.subjectScores[0].maximum_score}
                onChange={(e) => handleScoreChange(formData.subjectScores[0].subject_id, e.target.value)}
                className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-500 ${isRTL ? 'text-right' : 'text-left'}`}
                required
              />
            </div>
            
            <div className={`flex ${isRTL ? 'flex-row-reverse' : ''} justify-end mt-4`}>
              <button
                type="button"
                onClick={() => setIsEditModalOpen(false)}
                className={`px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 ${isRTL ? 'ml-2' : 'mr-2'}`}
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-md shadow-sm hover:bg-teal-700"
              >
                {t('common.update')}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // Render delete confirmation modal
  const renderDeleteModal = () => {
    if (!isDeleteModalOpen || !currentConfig) return null;
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg shadow-lg w-full max-w-md">
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-800">{t('modals.confirmDelete.title')}</h2>
            <button onClick={() => setIsDeleteModalOpen(false)} className="text-gray-500 hover:text-gray-700">
              <X className="w-6 h-6" />
            </button>
          </div>
          
          <div className="p-4">
            <p className={`text-gray-700 mb-4 ${isRTL ? 'text-right' : 'text-left'}`}>
              {t('modals.confirmDelete.message', {
                subject: getSubjectName(currentConfig.subject_id),
                class: getClassName(currentConfig.class_id)
              })}
            </p>
            
            <div className={`flex ${isRTL ? 'flex-row-reverse' : ''} justify-end mt-4`}>
              <button
                type="button"
                onClick={() => setIsDeleteModalOpen(false)}
                className={`px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 ${isRTL ? 'ml-2' : 'mr-2'}`}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={handleDelete}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md shadow-sm hover:bg-red-700"
              >
                {t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render delete class configs confirmation modal
  const renderDeleteClassConfigsModal = () => {
    if (!isDeleteClassConfigsModalOpen || !classConfigToDelete) return null;
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg shadow-lg w-full max-w-md">
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-800">{t('modals.confirmDelete.title')}</h2>
            <button onClick={() => setIsDeleteClassConfigsModalOpen(false)} className="text-gray-500 hover:text-gray-700">
              <X className="w-6 h-6" />
            </button>
          </div>
          
          <div className="p-4">
            <p className={`text-gray-700 mb-4 ${isRTL ? 'text-right' : 'text-left'}`}>
              {t('modals.confirmDeleteClass.message', {
                class: getClassName(classConfigToDelete)
              })}
            </p>
            
            <div className={`flex ${isRTL ? 'flex-row-reverse' : ''} justify-end mt-4`}>
              <button
                type="button"
                onClick={() => setIsDeleteClassConfigsModalOpen(false)}
                className={`px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 ${isRTL ? 'ml-2' : 'mr-2'}`}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={handleDeleteClassConfigs}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md shadow-sm hover:bg-red-700"
              >
                {t('buttons.deleteAll')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-700"></div>
      </div>
    );
  }

  return (
    <div className={`container mx-auto px-4 py-6 ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      {renderNotification()}
      
      <div className="bg-white rounded-lg shadow-lg p-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6">
          <div>
            <h1 className={`text-2xl font-bold text-gray-800 mb-2 ${isRTL ? 'text-right' : 'text-left'}`}>
              {exam?.exam_name} - {t('titles.subjectConfigurations')}
            </h1>
            <p className={`text-gray-600 ${isRTL ? 'text-right' : 'text-left'}`}>
              {t('descriptions.configureMaximumScores')}
            </p>
          </div>
          
          <button
            onClick={openCreateModal}
            className={`mt-4 md:mt-0 px-4 py-2 bg-teal-600 text-white font-medium rounded-md shadow hover:bg-teal-700 transition-colors flex items-center justify-center ${isRTL ? 'flex-row-reverse' : ''}`}
          >
            <Plus className={`w-5 h-5 ${isRTL ? 'ml-1' : 'mr-1'}`} />
            {t('buttons.addNewConfiguration')}
          </button>
        </div>
        
        {/* Class filter buttons */}
        <div className="mb-6">
          <h2 className={`text-lg font-medium text-gray-700 mb-2 ${isRTL ? 'text-right' : 'text-left'}`}>
            {t('filters.filterByClass')}
          </h2>
          <div className={`flex flex-wrap gap-2 ${isRTL ? 'justify-end' : 'justify-start'}`}>
            {classes.map(cls => (
              <button
                key={cls.id}
                onClick={() => handleClassFilter(cls.id)}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  selectedClass === cls.id
                    ? 'bg-teal-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {cls.name}
              </button>
            ))}
            {selectedClass && (
              <button
                onClick={() => setSelectedClass(null)}
                className="px-4 py-2 rounded-md text-sm font-medium bg-gray-200 text-gray-700 hover:bg-gray-300"
              >
                {t('filters.clearFilter')}
              </button>
            )}
          </div>
        </div>
        
        {/* Configurations */}
        {Object.keys(filteredConfigs).length > 0 ? (
          Object.entries(filteredConfigs).map(([classId, configs]) => (
            <div key={classId} className="mb-8 bg-gray-50 rounded-lg p-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className={`text-lg font-semibold text-gray-800 ${isRTL ? 'text-right' : 'text-left'}`}>
                  {getClassName(parseInt(classId))}
                </h3>
                <button
                  onClick={() => openDeleteClassConfigsModal(parseInt(classId))}
                  className={`px-3 py-1 text-sm text-red-600 border border-red-300 rounded-md hover:bg-red-50 flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}
                >
                  <Trash2 className={`w-4 h-4 ${isRTL ? 'ml-1' : 'mr-1'}`} />
                  {t('buttons.deleteAll')}
                </button>
              </div>
              
              <div className="overflow-x-auto">
                <table className={`w-full text-sm text-gray-700 ${isRTL ? 'text-right' : 'text-left'}`}>
                  <thead className="text-xs text-gray-700 uppercase bg-gray-100">
                    <tr>
                      <th className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'}`}>
                        {t('table.subject')}
                      </th>
                      <th className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'}`}>
                        {t('table.maximumScore')}
                      </th>
                      <th className={`px-6 py-3 ${isRTL ? 'text-left' : 'text-right'}`}>
                        {t('table.actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {configs.map(config => (
                      <tr key={config.id} className="bg-white border-b hover:bg-gray-50">
                        <td className="px-6 py-4">{getSubjectName(config.subject_id)}</td>
                        <td className="px-6 py-4">{config.maximum_score}</td>
                        <td className={`px-6 py-4 ${isRTL ? 'text-left' : 'text-right'}`}>
                          <button
                            onClick={() => openEditModal(config)}
                            className={`text-blue-600 hover:text-blue-900 ${isRTL ? 'ml-3' : 'mr-3'}`}
                          >
                            <Edit className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => openDeleteModal(config)}
                            className="text-red-600 hover:text-red-900"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-8">
            <p className={`text-gray-500 mb-4 ${isRTL ? 'text-right' : 'text-left'}`}>
              {t('messages.noConfigurationsFound')}
            </p>
            <button
              onClick={openCreateModal}
              className="px-4 py-2 bg-teal-600 text-white font-medium rounded-md shadow hover:bg-teal-700 transition-colors"
            >
              {t('buttons.createNewConfiguration')}
            </button>
          </div>
        )}
      </div>
      
      {renderCreateModal()}
      {renderEditModal()}
      {renderDeleteModal()}
      {renderDeleteClassConfigsModal()}
    </div>
  );
}

export default ExamConfigManager;